/**
 * @fileoverview User-invitation API calls.
 * @description Everything the Invite User form and the Invitations list call: the option lists
 * (GET /identity/configuration, and the assignable roles and eligible reporting managers, which
 * the shared directory-options service fetches), sending an invitation (POST /identity/invitations), and
 * listing, resending and revoking them (GET /identity/invitations, POST
 * /identity/invitations/{id}/resend and /revoke). All require USER.INVITE; the backend also only
 * shows an admin the invitations their own authority covers.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { OPTION_LIST_LIMIT } from '@/shared/api/directory-options.service'
import { httpClient } from '@/shared/api/http-client'
import { toPaginated } from '@/shared/api/pagination.utils'

import type {
  EligibleDepartments,
  IdentityConfiguration,
  InvitationListFilters,
  InvitationRevokeReason,
  InvitationSummary,
  ResendInvitationResponse,
  RevokeInvitationResponse,
  SendInvitationRequest,
  SendInvitationResponse,
} from '@/features/user-invitations/types/user-invitations.types'
import type { Paginated } from '@/shared/types/http.types'

/**
 * @description Fetches the invitation screens' option lists (statuses, revoke reasons).
 * @returns {Promise<IdentityConfiguration>} The option lists.
 */
export async function getIdentityConfiguration(): Promise<IdentityConfiguration> {
  const { data } = await httpClient.get<IdentityConfiguration>('/identity/configuration')
  return data
}

/**
 * @description Fetches the departments an invitee with the given roles may be placed in, with the
 * fixed or default one to preselect.
 * @param {string[]} roleIds - The roles being invited.
 * @returns {Promise<EligibleDepartments>} The departments and how one is chosen.
 */
export async function getEligibleDepartments(roleIds: string[]): Promise<EligibleDepartments> {
  const { data } = await httpClient.get<EligibleDepartments>('/users/eligible-departments', {
    params: { roleIds: roleIds.join(','), limit: OPTION_LIST_LIMIT },
  })
  return data
}

/**
 * @description Sends an invitation.
 * @param {SendInvitationRequest} payload - The invitee and their role plan.
 * @returns {Promise<SendInvitationResponse>} The created invitation's id and expiry.
 */
export async function sendInvitation(
  payload: SendInvitationRequest,
): Promise<SendInvitationResponse> {
  const { data } = await httpClient.post<SendInvitationResponse>('/identity/invitations', payload)
  return data
}

/**
 * @description Lists one page of the invitations the signed-in admin may manage, newest first.
 * @param {InvitationListFilters} filters - Page, page size, and optional status/search filters.
 * @returns {Promise<Paginated<InvitationSummary>>} The page and its meta.
 */
export async function getInvitations(
  filters: InvitationListFilters,
): Promise<Paginated<InvitationSummary>> {
  const response = await httpClient.get<InvitationSummary[]>('/identity/invitations', {
    params: {
      page: filters.page,
      limit: filters.limit,
      sort: '-createdAt',
      ...(filters.status && { status: filters.status }),
      ...(filters.search && { search: filters.search }),
    },
  })
  return toPaginated(response)
}

/**
 * @description Finds the invitation Resend and Revoke act on for a not-yet-active user: their
 * newest PENDING or EXPIRED one. The user directory doesn't carry invitation ids, so this searches
 * the invitation list by the user's email and matches the user id.
 * @param {{ userId: string, email: string }} user - The invited user.
 * @returns {Promise<InvitationSummary | null>} The invitation, or null when there is none.
 */
export async function findManageableInvitation(user: {
  userId: string
  email: string
}): Promise<InvitationSummary | null> {
  const { items } = await getInvitations({ page: 1, limit: 20, search: user.email })
  return (
    items.find(
      (invitation) =>
        invitation.userId === user.userId &&
        (invitation.status === 'PENDING' || invitation.status === 'EXPIRED'),
    ) ?? null
  )
}

/**
 * @description Re-sends an invitation with a fresh link (the old link stops working).
 * @param {string} invitationId - The invitation to resend.
 * @returns {Promise<ResendInvitationResponse>} Its new expiry.
 */
export async function resendInvitation(invitationId: string): Promise<ResendInvitationResponse> {
  const { data } = await httpClient.post<ResendInvitationResponse>(
    `/identity/invitations/${invitationId}/resend`,
    {},
  )
  return data
}

/**
 * @description Revokes an invitation.
 * @param {string} invitationId - The invitation to revoke.
 * @param {InvitationRevokeReason} reason - Why it's being revoked.
 * @returns {Promise<RevokeInvitationResponse>} Confirmation.
 */
export async function revokeInvitation(
  invitationId: string,
  reason: InvitationRevokeReason,
): Promise<RevokeInvitationResponse> {
  const { data } = await httpClient.post<RevokeInvitationResponse>(
    `/identity/invitations/${invitationId}/revoke`,
    { reason },
  )
  return data
}
